import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  aggregateAdminNotifications,
  DISMISS_KEY,
  loadIdSet,
  READ_KEY,
  saveIdSet,
  type AdminNotification,
} from '../services/admin-notification-aggregator';

type FilterChannel = 'all' | 'inbox' | 'activity' | 'unread';

type NotificationCenterValue = {
  items: AdminNotification[];
  loading: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  refresh: (force?: boolean) => Promise<void>;
  unreadCount: number;
  isRead: (id: string) => boolean;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  filter: FilterChannel;
  setFilter: (f: FilterChannel) => void;
  visibleItems: AdminNotification[];
};

const NotificationCenterContext = createContext<NotificationCenterValue | null>(null);

/** Shared across remounts so AdminLayout remounts don't hammer the API. */
let notifCache: { at: number; items: AdminNotification[] } | null = null;
const NOTIF_TTL_MS = 45_000;

export const NotificationCenterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<AdminNotification[]>(() => notifCache?.items ?? []);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(() => loadIdSet(READ_KEY));
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadIdSet(DISMISS_KEY));
  const [filter, setFilter] = useState<FilterChannel>('all');

  const refresh = useCallback(async (force = false) => {
    if (!force && notifCache && Date.now() - notifCache.at < NOTIF_TTL_MS) {
      setItems(notifCache.items);
      return;
    }
    setLoading(true);
    try {
      const next = await aggregateAdminNotifications();
      notifCache = { at: Date.now(), items: next };
      setItems(next);
    } catch {
      /* rate-limit / network — keep last good list */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(false);
  }, [refresh]);

  useEffect(() => {
    if (!drawerOpen) return;
    const tick = () => {
      if (document.hidden) return;
      void refresh(true);
    };
    const id = window.setInterval(tick, 45000);
    return () => window.clearInterval(id);
  }, [drawerOpen, refresh]);

  const isRead = useCallback((id: string) => readIds.has(id), [readIds]);

  const markRead = useCallback((id: string) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveIdSet(READ_KEY, next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setReadIds((prev) => {
      const next = new Set(prev);
      items.forEach((i) => next.add(i.id));
      saveIdSet(READ_KEY, next);
      return next;
    });
  }, [items]);

  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveIdSet(DISMISS_KEY, next);
      return next;
    });
  }, []);

  const activeItems = useMemo(
    () => items.filter((i) => !dismissed.has(i.id)),
    [items, dismissed]
  );

  const unreadCount = useMemo(
    () => activeItems.filter((i) => !readIds.has(i.id)).length,
    [activeItems, readIds]
  );

  const visibleItems = useMemo(() => {
    return activeItems.filter((i) => {
      if (filter === 'inbox') return i.channel === 'inbox';
      if (filter === 'activity') return i.channel === 'activity';
      if (filter === 'unread') return !readIds.has(i.id);
      return true;
    });
  }, [activeItems, filter, readIds]);

  const value = useMemo(
    () => ({
      items: activeItems,
      loading,
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      refresh,
      unreadCount,
      isRead,
      markRead,
      markAllRead,
      dismiss,
      filter,
      setFilter,
      visibleItems,
    }),
    [
      activeItems,
      loading,
      drawerOpen,
      refresh,
      unreadCount,
      isRead,
      markRead,
      markAllRead,
      dismiss,
      filter,
      visibleItems,
    ]
  );

  return (
    <NotificationCenterContext.Provider value={value}>{children}</NotificationCenterContext.Provider>
  );
};

export function useNotificationCenter(): NotificationCenterValue {
  const ctx = useContext(NotificationCenterContext);
  if (!ctx) {
    throw new Error('useNotificationCenter must be used within NotificationCenterProvider');
  }
  return ctx;
}
